import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { EnvironmentService } from '../services/enviroment.service';
import { environment } from '../services/global-environment';
import { MfaCheckResponse } from '../models/mfa.model';
import { I18nService } from './i18n.service';

import { Router } from '@angular/router';
import { MfaService } from './mfa.service';

// 定義 API 回應介面
interface UserPermissionApiResponse {
  success: boolean;
  data: string[];
  message?: string;
}

// 定義角色選項介面
export interface RoleOption {
  label: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl; // API 端點

  constructor(
    private http: HttpClient,
    private router: Router,
    private mfaService: MfaService,
    private i18nService: I18nService
  ) {}

  // 角色選項設定（支援多國語系）
  get roleOptions(): RoleOption[] {
    return [
      { label: this.i18nService.translate('role.super_admin'), value: 'super_admin' },
      { label: this.i18nService.translate('role.admin'), value: 'admin' },
      { label: this.i18nService.translate('role.user'), value: 'user' }
    ];
  }

  // 檢查使用者是否已登入
  isLoggedIn(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token; // 若 token 存在，返回 true
  }

  // 取得使用者資訊
  getUserInfo() {
    return {
      username: localStorage.getItem('username') || '',
      companyid: localStorage.getItem('companyid') || '',
      modules: JSON.parse(localStorage.getItem('modules') || '[]'),
      userid: localStorage.getItem('userid') || '',
    };
  }

  // 載入使用者權限 - 取得使用者可瀏覽的驗證群組
  load_user_permission(assistantNo:string): Promise<string[]> {
    return new Promise((resolve, reject) => {
      // 取得當前使用者資訊
      const userInfo = this.getUserInfo();
      const { companyid, userid } = userInfo;

      // 檢查必要參數
      if (!companyid || !userid) {
        console.error('無法取得使用者權限：缺少 companyid 或 userid');
        resolve([]); // 回傳空陣列而非拋出錯誤，避免影響後續流程
        return;
      }

      // 從環境或其他來源取得 assistantNo
      // 注意：您可能需要根據實際情況調整這個參數的取得方式

      // 建立 API URL
      const url = `${this.apiUrl}/api/departments/user-departments/${companyid}/${assistantNo}/${userid}`;

      console.log(`載入使用者權限，URL: ${url}`);

      // 呼叫 API 取得使用者可存取的部門ID清單
      this.http.get<UserPermissionApiResponse>(url).subscribe({
        next: (response) => {
          if (response.success && response.data && Array.isArray(response.data)) {
            console.log('成功取得使用者權限部門清單:', response.data);
            resolve(response.data);
          } else {
            console.warn('API 回應成功但無有效資料:', response);
            resolve([]); // 回傳空陣列
          }
        },
        error: (error) => {
          console.error('取得使用者權限時發生錯誤:', error);
          // 不拋出錯誤，而是回傳空陣列，避免影響其他功能
          resolve([]);
        }
      });
    });
  }

  // 檢查是否有指定模組的權限
  hasModuleAccess(moduleId: string): boolean {
    if (!this.isLoggedIn()) return false;

    const modulesStr = localStorage.getItem('modules');
    if (!modulesStr) return false;

    try {
      const modules = JSON.parse(modulesStr);

      // 檢查格式，若是新格式(物件陣列)
      if (modules.length > 0 && typeof modules[0] === 'object') {
        // 檢查是否有 ALL 權限或指定模組權限
        return modules.some((m: any) =>
          m.assistantNo === 'ALL' || m.assistantNo === moduleId
        );
      }
      // 舊格式(字串陣列)
      else {
        return modules.includes('ALL') || modules.includes(moduleId);
      }
    } catch (e) {
      console.error('Error parsing modules from localStorage:', e);
      return false;
    }
  }

  // 重新導向到登入頁
  redirectToLogin() {
    this.router.navigate(['/login']);
  }

  login(credentials: { username: string; password: string }): Observable<any> {

    const test_account = environment.test_account;

    if (
      credentials.username === test_account &&  credentials.password === test_account
    ) {
      // 模擬成功的回應
      return of({ success: true, token: 'fake-jwt-token' });
    } else {
      // 模擬失敗的回應
      return throwError(() => new Error('Invalid username or password'));
    }
  }

  getJwtToken(username: string): Observable<{ accessToken: string }> {
    return this.http.post<{ accessToken: string }>(
      `${this.apiUrl}/api/auth/token`,
      { username },
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }
    );
  }

  // 🔹 儲存 Token 到 localStorage
  storeToken(token: string,companyid: string, username: string, modules: string[],id:string) : void{
    localStorage.setItem('authToken', token);
    localStorage.setItem('companyid', companyid);
    localStorage.setItem('username', username);
    localStorage.setItem('modules', JSON.stringify(modules));
    localStorage.setItem('userid', id);
  }

  // 🔹 清除 Token (登出)
  clearToken() {
    localStorage.clear();
    // Reset MFA state on logout
    this.mfaService.resetSetupState();
    this.mfaService.updateMfaStatus({ isEnabled: false, hasBackupCodes: false });
  }

  // 🔹 檢查使用者是否需要 MFA 驗證
  checkMfaRequired(username: string, companyid: string): Observable<MfaCheckResponse> {
    // This would be implemented based on backend response
    // For now, we'll simulate checking if user has MFA enabled
    return this.http.post<MfaCheckResponse>(
      `${this.apiUrl}/api/auth/mfa/check-mfa`,
      { username, companyid },
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }
    ).pipe(
      catchError(() => of({
        success: false,
        data: { mfaRequired: false, mfaSetupRequired: false },
        message: 'Error checking MFA requirement'
      })) // Default to no MFA required on error
    );
  }

  // 🔹 更新使用者 MFA 狀態
  loadUserMfaStatus(username: string, companyid: string): void {
    // This would load the user's MFA status from the backend
    // For now, we'll check localStorage or make an API call
    this.http.post<{ isEnabled: boolean, hasBackupCodes: boolean, setupDate?: string }>(
      `${this.apiUrl}/api/auth/mfa/status`,
      { username, companyid },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        })
      }
    ).subscribe({
      next: (status) => {
        this.mfaService.updateMfaStatus(status);
      },
      error: (error) => {
        console.error('Failed to load MFA status:', error);
        // Default to MFA disabled if we can't load status
        this.mfaService.updateMfaStatus({ isEnabled: false, hasBackupCodes: false });
      }
    });
  }

}
